import moment from "moment";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "react-query";
import Swal from "sweetalert2";
import CustomTable from "../../../Shared/CustomTable";
import { apiConnectorGet, apiConnectorPost } from "../../../utils/APIConnector";
import { endpoint } from "../../../utils/APIRoutes";
import { areYouSureFn, formatedDate } from "../../../utils/utilityFun";

const MT5AccountIDS = () => {
  const client = useQueryClient();
  const [newAccountNo, setNewAccountNo] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [editAccountNo, setEditAccountNo] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const { data, isLoading, isFetching } = useQuery(
    ["get_mt5_account_admin"],
    () => apiConnectorGet(endpoint?.get_mt5_account_admin),
    {
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      onError: (err) => console.error("Error fetching MT5 account list:", err),
    },
  );

  const allData = useMemo(() => {
    const result = data?.data?.result;
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result?.data;
    if (Array.isArray(data?.data?.data)) return data?.data?.data;
    return [];
  }, [data]);

  const refreshList = () => {
    client.invalidateQueries(["get_mt5_account_admin"]);
  };

  const handleCreateAccount = async () => {
    const accountNo = newAccountNo?.trim();
    if (!accountNo) {
      toast.error("Please enter account number.");
      return;
    }

    setCreateLoading(true);
    try {
      const res = await apiConnectorPost(endpoint?.create_mt5_account_admin, {
        account_no: accountNo,
      });
      if (res?.data?.status === false) {
        toast.error(res?.data?.message || "Failed to create account.");
      } else {
        toast.success(
          res?.data?.message || "MT5 account created successfully.",
        );
        setNewAccountNo("");
        refreshList();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to create account.",
      );
    }
    setCreateLoading(false);
  };

  const openEdit = (row) => {
    setSelectedRow(row);
    setEditAccountNo(row?.m07_ac_no || "");
    setOpenEditDialog(true);
  };

  const closeEdit = () => {
    setOpenEditDialog(false);
    setSelectedRow(null);
    setEditAccountNo("");
  };

  const handleUpdateAccount = async () => {
    const accountNo = editAccountNo?.trim();
    if (!selectedRow?.m07_ac_id) {
      toast.error("Invalid account selected.");
      return;
    }
    if (!accountNo) {
      toast.error("Please enter account number.");
      return;
    }

    setUpdateLoading(true);
    try {
      const res = await apiConnectorPost(endpoint?.update_mt5_account_admin, {
        pk: selectedRow?.m07_ac_id,
        account_no: accountNo,
      });
      if (res?.data?.status === false) {
        toast.error(res?.data?.message || "Failed to update account.");
      } else {
        toast.success(
          res?.data?.message || "MT5 account updated successfully.",
        );
        closeEdit();
        refreshList();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update account.",
      );
    }
    setUpdateLoading(false);
  };

  const handleToggleStatus = async (row) => {
    const pk = row?.m07_ac_id;
    if (!pk) {
      toast.error("Invalid account id.");
      return;
    }

    setStatusLoadingId(pk);
    try {
      const res = await apiConnectorPost(endpoint?.change_mt5_account_status, {
        pk,
      });
      if (res?.data?.status === false) {
        toast.error(res?.data?.message || "Failed to update status.");
      } else {
        toast.success(res?.data?.message || "Status updated successfully.");
        refreshList();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status.");
    }
    setStatusLoadingId(null);
  };

  const handleDeleteAccount = async (row) => {
    const pk = row?.m07_ac_id;
    if (!pk) {
      toast.error("Invalid account id.");
      return;
    }

    setDeleteLoadingId(pk);
    try {
      const res = await apiConnectorPost(endpoint?.delete_mt5_account_admin, {
        pk,
      });
      if (res?.data?.status === false) {
        toast.error(res?.data?.message || "Failed to delete account.");
      } else {
        toast.success(
          res?.data?.message || "MT5 account deleted successfully.",
        );
        refreshList();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to delete account.",
      );
    }
    setDeleteLoadingId(null);
  };

  const tablehead = [
    <span>S.No.</span>,
    <span>Account ID</span>,
    <span>Account No</span>,
    <span>Status</span>,
    <span>Created At</span>,
    <span>Updated At</span>,
    <span>Edit</span>,
    <span>Delete</span>,
  ];

  const tablerow = allData?.map((row, index) => {
    const isActive = Number(row?.m07_ac_status) === 1;
    const isStatusLoading = statusLoadingId === row?.m07_ac_id;
    const isDeleteLoading = deleteLoadingId === row?.m07_ac_id;

    return [
      <span>{index + 1}</span>,
      <span>{row?.m07_ac_id || "--"}</span>,
      <span>{row?.m07_ac_no || "--"}</span>,
      <label className="inline-flex items-center cursor-pointer gap-2">
        <input
          type="checkbox"
          checked={isActive}
          onChange={() => handleToggleStatus(row)}
          disabled={isStatusLoading}
          className="sr-only peer"
        />
        <div className="relative w-10 h-5 bg-gray-600 rounded-full peer peer-checked:bg-cyan-600 transition-colors">
          <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
        </div>
        <span className="text-xs text-gray-300">
          {isStatusLoading ? "Updating..." : isActive ? "Active" : "Inactive"}
        </span>
      </label>,
      <span>{formatedDate(moment, row?.m07_ac_created_at)}</span>,
      <span>{formatedDate(moment, row?.m07_ac_updated_at)}</span>,
      <button
        type="button"
        onClick={() => openEdit(row)}
        className="px-3 py-1 rounded-md text-xs font-medium bg-cyan-600 hover:bg-cyan-500 transition-colors !text-white"
      >
        Edit
      </button>,
      <button
        type="button"
        onClick={() => areYouSureFn(Swal, () => handleDeleteAccount(row))}
        disabled={isDeleteLoading}
        className="px-3 py-1 rounded-md text-xs font-medium bg-red-600 hover:bg-red-500 disabled:opacity-60 transition-colors !text-white"
      >
        {isDeleteLoading ? "Deleting..." : "Delete"}
      </button>,
    ];
  });

  return (
    <div className="p-2 space-y-4">
      <div className="rounded-lg p-3 bg-black border border-gray-700 shadow-md shadow-white/10">
        <h3 className="text-white text-sm font-semibold mb-3">
          Create MT5 Account
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="text-xs text-gray-300">Account Number</label>
            <input
              type="text"
              value={newAccountNo}
              onChange={(e) => setNewAccountNo(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-600 bg-black px-3 py-2 text-sm text-white outline-none focus:border-cyan-500"
              placeholder="Enter MT5 account number"
            />
          </div>
          <button
            type="button"
            onClick={() => areYouSureFn(Swal, () => handleCreateAccount())}
            disabled={createLoading}
            className="h-10 px-4 rounded-md text-sm bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 text-white"
          >
            {createLoading ? "Creating..." : "Create"}
          </button>
        </div>
      </div>

      <div className="rounded-lg py-3 text-white bg-black border border-gray-700 shadow-md shadow-white/20">
        <CustomTable
          tablehead={tablehead}
          tablerow={tablerow}
          isLoading={isLoading || isFetching}
        />
      </div>

      {openEditDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md rounded-lg border border-gray-700 bg-[#0f0f0f] p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-white">
                Update MT5 Account
              </h3>
              <button
                type="button"
                onClick={closeEdit}
                className="text-gray-400 hover:text-white"
              >
                X
              </button>
            </div>

            <div className="space-y-2">
              <label className="text-xs text-gray-300">Account Number</label>
              <input
                type="text"
                value={editAccountNo}
                onChange={(e) => setEditAccountNo(e.target.value)}
                className="w-full rounded-md border border-gray-600 bg-black px-3 py-2 text-sm text-white outline-none focus:border-cyan-500"
                placeholder="Enter account number"
              />
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeEdit}
                className="px-4 py-2 rounded-md text-sm bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => areYouSureFn(Swal, () => handleUpdateAccount())}
                disabled={updateLoading}
                className="px-4 py-2 rounded-md text-sm bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60"
              >
                {updateLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MT5AccountIDS;
